/**
 * useTransactionCustomFieldsSettings Hook
 *
 * Loads and saves transactionCustomFields in workspace settings (Firestore).
 * Uses cache so Transaction Fields section opens instantly when preloaded.
 */

import { useEffect, useState, useCallback } from "react";
import { db } from "../../../firebase";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import type {
  TransactionCustomField,
  WorkspaceSettings,
} from "../../../types";

const WORKSPACE_SETTINGS_DOC_ID = "general";

const fieldsCache = new Map<string, { data: TransactionCustomField[]; timestamp: number }>();
const CACHE_TTL_MS = 90 * 1000;

export interface UseTransactionCustomFieldsSettingsParams {
  workspaceId: string;
}

export interface UseTransactionCustomFieldsSettingsResult {
  fields: TransactionCustomField[];
  loading: boolean;
  error: string | null;
  setFields: (fields: TransactionCustomField[]) => void;
  saveFields: (fields: TransactionCustomField[]) => Promise<void>;
}

/** Omit undefined from an object so Firestore doesn't reject it */
function sanitizeField(
  f: TransactionCustomField
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const keys = Object.keys(f) as (keyof TransactionCustomField)[];
  for (const key of keys) {
    if (typeof key !== "string") continue;
    const v = f[key as keyof TransactionCustomField];
    if (v !== undefined) {
      out[key] = v;
    }
  }
  return out;
}

export function useTransactionCustomFieldsSettings({
  workspaceId,
}: UseTransactionCustomFieldsSettingsParams): UseTransactionCustomFieldsSettingsResult {
  const cached = workspaceId ? fieldsCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);

  const [fields, setFields] = useState<TransactionCustomField[]>(hasValidCache && cached ? cached.data : []);
  const [loading, setLoading] = useState(!hasValidCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      setFields([]);
      return;
    }
    const settingsRef = doc(
      db,
      "workspaces",
      workspaceId,
      "settings",
      WORKSPACE_SETTINGS_DOC_ID
    );
    const unsubscribe = onSnapshot(
      settingsRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as WorkspaceSettings;
          const list = data.transactionCustomFields ?? [];
          const arr = Array.isArray(list) ? list : [];
          const sorted = [...arr].sort(
            (a: TransactionCustomField, b: TransactionCustomField) =>
              (a.order ?? 0) - (b.order ?? 0)
          );
          if (workspaceId) fieldsCache.set(workspaceId, { data: sorted, timestamp: Date.now() });
          setFields(sorted);
        } else {
          if (workspaceId) fieldsCache.set(workspaceId, { data: [], timestamp: Date.now() });
          setFields([]);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Transaction custom fields settings snapshot error:", err);
        setError(err.message ?? "Failed to load transaction fields");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [workspaceId]);

  const saveFields = useCallback(
    async (nextFields: TransactionCustomField[]) => {
      if (!workspaceId) return;
      const settingsRef = doc(
        db,
        "workspaces",
        workspaceId,
        "settings",
        WORKSPACE_SETTINGS_DOC_ID
      );
      const sanitized = nextFields.map((f) => sanitizeField(f));
      await updateDoc(settingsRef, { transactionCustomFields: sanitized });
      fieldsCache.set(workspaceId, { data: nextFields, timestamp: Date.now() });
      setFields(nextFields);
    },
    [workspaceId]
  );

  return {
    fields,
    loading,
    error,
    setFields,
    saveFields,
  };
}
